import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useInvitationStore } from '../../lib/store/invitationStore';
import { useAuth } from '../../context/useAuth';
import { Alert, Button, Card } from '../../components/common';

export default function AcceptInvitationPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { currentInvitation, isLoading, error, fetchInvitationByToken, acceptInvitation, declineInvitation } = useInvitationStore();
  const { isAuthenticated } = useAuth();
  const [actionError, setActionError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [isDeclined, setIsDeclined] = useState(false);
  
  // Debug the token extracted from URL
  useEffect(() => {
    console.log('Invitation token from URL:', token);
  }, [token]);
  
  useEffect(() => {
    if (token) {
      console.log('Fetching invitation with token:', token);
      fetchInvitationByToken(token);
    } else {
      console.error('No token provided in URL');
    }
  }, [token, fetchInvitationByToken]);
  
  const handleAccept = async () => {
    if (!token) return;
    
    setIsProcessing(true);
    setActionError(null);
    
    try {
      await acceptInvitation(token);
      setIsAccepted(true);
      
      // Redirect to team dashboard after a delay
      setTimeout(() => {
        if (currentInvitation) {
          navigate(`/teams/${currentInvitation.team_id}/dashboard`);
        } else {
          navigate('/dashboard');
        }
      }, 3000);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to accept invitation. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleDecline = async () => {
    if (!token) return;
    
    setIsProcessing(true);
    setActionError(null);
    
    try {
      await declineInvitation(token);
      setIsDeclined(true);
      
      // Redirect to dashboard after a delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to decline invitation. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Check if user needs to sign in first
  if (!isLoading && currentInvitation && !isAuthenticated) {
    return (
      <div className="max-w-md mx-auto">
        <Card className="p-6">
          <h1 className="text-2xl font-bold mb-4 text-center">Sign in Required</h1>
          <p className="text-gray-600 mb-6 text-center">
            You need to sign in to accept the invitation to join {currentInvitation.team?.name}.
          </p>
          <Button 
            variant="primary" 
            className="w-full"
            onClick={() => navigate(`/login?redirect=/invitations/${token}`)}
          >
            Sign In
          </Button>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="max-w-md mx-auto px-4">
      <Card className="p-6">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin h-8 w-8 border-t-2 border-b-2 border-primary rounded-full"></div>
          </div>
        ) : error ? (
          <div className="text-center py-6">
            <div className="text-3xl mb-2">⚠️</div>
            <h1 className="text-xl font-bold mb-2">Invalid Invitation</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button 
              variant="primary" 
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </Button>
          </div>
        ) : currentInvitation ? (
          isAccepted ? (
            <div className="text-center py-6">
              <div className="text-3xl mb-2">✅</div>
              <h1 className="text-xl font-bold mb-2">Invitation Accepted</h1>
              <p className="text-gray-600 mb-6">
                You have successfully joined {currentInvitation.team?.name}. Redirecting to team dashboard...
              </p>
            </div>
          ) : isDeclined ? (
            <div className="text-center py-6">
              <div className="text-3xl mb-2">🚫</div>
              <h1 className="text-xl font-bold mb-2">Invitation Declined</h1>
              <p className="text-gray-600 mb-6">
                You have declined the invitation to join {currentInvitation.team?.name}. Redirecting to dashboard...
              </p>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold mb-2 text-center">Team Invitation</h1>
              
              <div className="text-center mb-6">
                <p className="text-gray-600">
                  You've been invited to join <span className="font-semibold">{currentInvitation.team?.name}</span> as a {currentInvitation.role}.
                </p>
                <p className="text-gray-500 text-sm mt-1">
                  Invited by {currentInvitation.invited_by_user?.full_name || 'a team administrator'}
                </p>
              </div>
              
              {actionError && (
                <Alert variant="error" className="mb-4">
                  {actionError}
                </Alert>
              )}
              
              <div className="space-y-3">
                <Button
                  variant="primary"
                  className="w-full"
                  isLoading={isProcessing && !isDeclined}
                  onClick={handleAccept}
                  disabled={isProcessing}
                >
                  Accept Invitation
                </Button>
                
                <Button
                  variant="outline"
                  className="w-full"
                  isLoading={isProcessing && isDeclined}
                  onClick={handleDecline}
                  disabled={isProcessing}
                >
                  Decline Invitation
                </Button>
              </div>
            </>
          )
        ) : null}
      </Card>
    </div>
  );
} 